import { format } from 'date-fns';
import { SubscriptionState } from '../types';
import { Address } from '../types/api';

// Helper functions for subscription management

// Format address for display
export const formatAddress = (address: Address | null | undefined): string => {
  if (!address) return '';
  
  const { street, city, state, zipCode, addressLabel } = address;
  // Use zipCode value
  const zipValue = zipCode || '';
  
  let formattedAddress = `${street || ''}, ${city || ''}, ${state || ''} ${zipValue}`.trim();
  
  // Add address label if available
  if (addressLabel) {
    formattedAddress = `${formattedAddress} (${addressLabel})`;
  }
  
  return formattedAddress;
};

// Convert day number to day name
export const getDayName = (dayOfWeek: number): string => {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return dayNames[dayOfWeek] || 'Unknown';
};

// Filter subscriptions by status
export const filterSubscriptions = (subscriptions: SubscriptionState[]) => {
  // Define active statuses - subscriptions with these statuses should be considered active
  const activeStatuses = ['active', 'paused', 'past_due'];
  
  // Define valid subscription statuses - exclude pending_payment but include past_due
  const validStatuses = ['active', 'paused', 'cancelled', 'past_due'];
  
  // Filter out subscriptions with invalid statuses including 'pending_payment'
  const filteredSubscriptions = subscriptions.filter(sub => 
    sub.status !== undefined && 
    sub.status !== null && 
    validStatuses.includes(sub.status)
  );
  
  // Filter active and cancelled/inactive subscriptions
  const active = filteredSubscriptions.filter(sub => activeStatuses.includes(sub.status || ''));
  const cancelled = filteredSubscriptions.filter(sub => sub.status === 'cancelled');
  
  return { active, cancelled };
};

// Format date for display
export const formatDate = (date: string | null | undefined): string => {
  if (!date) return 'Not scheduled';
  return format(new Date(date), 'MMMM d, yyyy');
};

// Simplify time slot display (e.g. "9:00 AM - 12:00 PM" to "9 AM - 12 PM")
export const simplifyTimeSlot = (timeSlot: string): string => {
  return timeSlot
    .replace(/(\d+):00/g, '$1') // Remove ":00" from times
    .replace(/ AM/g, ' AM') // These lines seem redundant but are kept for clarity
    .replace(/ PM/g, ' PM');
};

// Format multiple time slots with simplified format
export const formatSimplifiedTimeSlots = (timeSlots: string[]): string => {
  return timeSlots.map(slot => simplifyTimeSlot(slot)).join(', ');
}; 